/**
 * Gravações em série onde só o valor mais recente importa (ex.: ordem das abas
 * fixadas). Nunca há duas requisições em voo: enquanto uma roda, chamadas novas
 * substituem a pendente, e ela sai quando a atual termina. Assim uma resposta
 * antiga não sobrescreve no servidor uma ordem mais nova.
 *
 * `onError` só é chamado quando a gravação que falhou é a mais recente (não há
 * outra pendente que vá corrigir o estado).
 */
export function createLatestOnlyWriter<T>(
  send: (value: T) => Promise<unknown>,
  onError: (error: unknown) => void,
): (value: T) => void {
  let inFlight = false;
  let hasPending = false;
  let pending: T | undefined;

  const run = async (value: T): Promise<void> => {
    inFlight = true;
    try {
      await send(value);
    } catch (error) {
      if (!hasPending) onError(error);
    } finally {
      inFlight = false;
      if (hasPending) {
        const next = pending as T;
        hasPending = false;
        pending = undefined;
        void run(next);
      }
    }
  };

  return (value: T) => {
    if (inFlight) {
      pending = value;
      hasPending = true;
      return;
    }
    void run(value);
  };
}

/**
 * Reposiciona as abas fixadas na ordem `orderedPaths`, mantendo as não fixadas
 * onde estão (as fixadas trocam de lugar só entre si).
 */
export function applyPinnedOrder<T extends { path: string; isPinned: boolean }>(
  tabs: T[],
  orderedPaths: string[],
): T[] {
  const rank = new Map(orderedPaths.map((path, index) => [path, index]));
  const pinned = tabs
    .filter((tab) => tab.isPinned)
    .sort((a, b) => (rank.get(a.path) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.path) ?? Number.MAX_SAFE_INTEGER));
  let next = 0;
  return tabs.map((tab) => (tab.isPinned ? pinned[next++] : tab));
}
